import { get, post } from './API';

const fetchPartnerBookingsForSync = () => get('partner-integrations/sync');

/**
 * Drop the filters nobody picked. `get` spreads whatever it is handed
 * straight into axios' `params`, and axios omits `undefined` but happily
 * sends `decision=` for an empty string — which the API would read as a
 * filter on the empty decision rather than as "no filter".
 */
const definedParams = (params = {}) => Object.entries(params)
  .reduce((acc, [key, value]) => {
    if (value === undefined || value === null || value === '') return acc;
    return { ...acc, [key]: value };
  }, {});

/**
 * The auto-approval decision log.
 *
 * Unwrapped here rather than at the call site: the API wraps every response as
 * `{ success: true, data: <payload> }` and axios wraps that again in `.data`,
 * so the payload is at `response.data.data` — the same two hops
 * `fetchPartnerBookingsForSync`'s caller makes by hand in PartnerSync/index.
 * The decision view has two endpoints and a filter form, so the hops live in
 * one place instead of being repeated at every call.
 *
 * @param {{ partner_id?: string, decision?: string, since?: string, limit?: number }} params
 * @returns {Promise<{ rows: Array, total: number }>}
 */
export const fetchPartnerDecisions = async (params = {}) => {
  const response = await get('partner-integrations/decisions', definedParams(params));
  const payload = response?.data?.data;

  return {
    rows: Array.isArray(payload?.rows) ? payload.rows : [],
    total: Number.isFinite(Number(payload?.total)) ? Number(payload.total) : 0,
  };
};

/**
 * Counts by decision and by held reason over the same window.
 *
 * The contract fixes the endpoint but not the key names on the payload, and
 * the API side is being built in parallel, so both halves are read under
 * either of the two plausible spellings and an unreadable payload degrades to
 * empty counts rather than to a crash. If the shape lands differently again,
 * this is the one function to change.
 */
export const fetchPartnerDecisionSummary = async (params = {}) => {
  const response = await get('partner-integrations/decisions/summary', definedParams(params));
  const payload = response?.data?.data;

  const asCounts = (value) => (value && typeof value === 'object' && !Array.isArray(value) ? value : {});

  return {
    byDecision: asCounts(payload?.by_decision ?? payload?.decision),
    byHeldReason: asCounts(payload?.by_held_reason ?? payload?.held_reason),
  };
};

/**
 * The two answers "Run now" can get, and NEITHER OF THEM IS "it is done".
 *
 * `POST /partner-integrations/decisions` starts the decision-pass Lambda
 * asynchronously and returns at once — API Gateway's integration timeout is
 * 29 seconds and the pass is sized for 300, so the request cannot wait for it
 * and the handler says so in as many words ("Nothing has been decided yet").
 * The body carries `run_id` and no counts, because the request never saw a
 * decision.
 */
export const DECISION_PASS = { QUEUED: 'queued', ALREADY_RUNNING: 'already_running' };

/**
 * ASK FOR A DECISION PASS. It cannot approve anything.
 *
 * The endpoint starts the deployed pass by name — the same artefact an
 * arriving offer and the daily safety-net rule start — and changes only WHEN
 * it runs, never what it may conclude. `partner_ids` can narrow a run and
 * nothing in the request can widen it.
 *
 * A 409 IS NOT A FAILURE AND IS NOT THROWN. `BackgroundRunService` holds a
 * 60-second cooldown per purpose, which it states explicitly is a guard
 * against a double-click and NOT a lock. So a 409 means "one was started
 * moments ago and is still being given time" — an answer about the work, not
 * an error about the request — and it is returned as an outcome so the screen
 * can say that rather than render a red alert over a pass that is running.
 * Every other failure is rethrown, because that one the operator must see.
 *
 * The 409's details are spread flat into the body by the API's error
 * middleware (the same shape `tripClaimsRefusalOf` reads), so they are read
 * off `response.data` and not out of a nested `details`.
 */
export const startPartnerDecisionPass = async ({ partnerIds } = {}) => {
  const body = Array.isArray(partnerIds) && partnerIds.length > 0
    ? { partner_ids: partnerIds }
    : {};

  try {
    const response = await post('partner-integrations/decisions', body);

    return {
      status: DECISION_PASS.QUEUED,
      runId: response?.data?.data?.run_id || null,
    };
  } catch (error) {
    const conflict = error?.response?.status === 409 ? error.response.data : null;
    if (!conflict) throw error;

    const retry = Number(conflict.retry_after_seconds);

    return {
      status: DECISION_PASS.ALREADY_RUNNING,
      runId: conflict.run_id || null,
      startedBy: conflict.started_by || null,
      retryAfterSeconds: Number.isFinite(retry) ? retry : null,
    };
  }
};

export default fetchPartnerBookingsForSync;
